import { useTranslation } from 'react-i18next';
import {
  ANNOUNCEMENT_FONTS as F,
  ANNOUNCEMENT_PALETTE as P,
  announcementButtonBase,
  formatAnnouncementDate,
  type AnnouncementCardProps,
} from '../announcement-style';

const STYLES = `
@keyframes announcementIssueIn {
  from { opacity: 0; transform: translateY(16px); }
  to { opacity: 1; transform: translateY(0); }
}
.announcement-issue { padding: 26px 28px; }
.announcement-issue h2 { font-size: clamp(2rem, 7vw, 3.1rem); }
@container announcement (max-width: 700px) {
  .announcement-issue { padding: 20px 18px 22px; }
  .announcement-issue .announcement-issue-mast { font-size: 10px; }
}
`;

/** Splits the headline so its last word can be drawn in outline (zine cover). */
function splitHeadline(title: string): { head: string; last: string } {
  const words = title.trim().split(/\s+/);
  const last = words.pop() ?? '';
  return { head: words.join(' '), last };
}

/**
 * "Capa da edição" — the announcement as the red halftone cover of an issue
 * of the school magazine: masthead, issue line, giant condensed headline with
 * the last word outlined, serif italic dek, stamps and a paper-coloured CTA.
 */
export function IssueCoverCard(props: AnnouncementCardProps) {
  const { t } = useTranslation('p2e');
  const { head, last } = splitHeadline(props.title);

  return (
    <article
      role="dialog"
      aria-modal="true"
      aria-label={props.title}
      className="announcement-issue"
      onClick={(event) => event.stopPropagation()}
      style={{
        position: 'relative',
        width: '100%',
        maxWidth: 560,
        maxHeight: '90vh',
        overflowY: 'auto',
        color: P.paper,
        borderRadius: 3,
        backgroundColor: P.maxRed,
        backgroundImage: `radial-gradient(rgba(26,16,24,.16) 1px, transparent 1.5px), linear-gradient(180deg, ${P.maxRed}, ${P.redDeep})`,
        backgroundSize: '9px 9px, 100% 100%',
        boxShadow: '0 24px 60px rgba(0,0,0,.45)',
        animation: props.reducedMotion ? undefined : 'announcementIssueIn .35s ease both',
      }}
    >
      <style>{STYLES}</style>

      {/* tape corner deco, as on the class cover */}
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          width: 130,
          height: 34,
          background: 'rgba(248,215,221,.5)',
          top: 22,
          right: -36,
          transform: 'rotate(38deg)',
          pointerEvents: 'none',
        }}
      />

      <div
        className="announcement-issue-mast"
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          gap: 8,
          borderBottom: '2px solid rgba(255,248,245,.4)',
          paddingBottom: 11,
          marginBottom: 20,
          fontFamily: F.cond,
          textTransform: 'uppercase',
          letterSpacing: '.22em',
          fontSize: 11,
          fontWeight: 600,
        }}
      >
        <span style={{ letterSpacing: '.28em', fontWeight: 700 }}>{t('announcements.brand')}</span>
        <span>{t('announcements.masthead')}</span>
      </div>

      <p
        style={{
          fontFamily: F.cond,
          fontSize: 12,
          letterSpacing: '.3em',
          textTransform: 'uppercase',
          color: P.baby,
          margin: '0 0 10px',
        }}
      >
        {t('announcements.issueLine', { date: formatAnnouncementDate(props.date, props.locale, 'short') })}
      </p>

      <h2
        style={{
          fontFamily: F.cond,
          textTransform: 'uppercase',
          fontWeight: 700,
          lineHeight: 0.92,
          letterSpacing: '.005em',
          margin: '0 0 16px',
          textWrap: 'balance',
        }}
      >
        {head ? `${head} ` : null}
        {last && (
          <span style={{ color: 'transparent', WebkitTextStroke: `2px ${P.paper}` }}>{last}</span>
        )}
      </h2>

      {props.body && (
        <p
          style={{
            fontFamily: F.serif,
            fontStyle: 'italic',
            fontSize: 16,
            lineHeight: 1.5,
            color: P.dek,
            margin: '0 0 18px',
            maxWidth: '44ch',
            whiteSpace: 'pre-line',
          }}
        >
          {props.body}
        </p>
      )}

      {props.media && <div style={{ marginBottom: 18 }}>{props.media}</div>}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 20 }}>
        <span
          style={{
            fontFamily: F.cond,
            fontSize: 10,
            fontWeight: 600,
            letterSpacing: '.2em',
            textTransform: 'uppercase',
            border: '1.5px solid rgba(255,248,245,.65)',
            borderRadius: 999,
            padding: '5px 13px',
          }}
        >
          {t('announcements.badgeNew')}
        </span>
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
        <button
          ref={props.primaryRef}
          type="button"
          disabled={props.primaryDisabled}
          onClick={props.onPrimary}
          style={{
            ...announcementButtonBase,
            background: P.paper,
            color: P.maxRed,
            opacity: props.primaryDisabled ? 0.45 : 1,
            cursor: props.primaryDisabled ? 'default' : 'pointer',
          }}
        >
          {props.primaryLabel}
        </button>
        {props.skippable && (
          <button
            type="button"
            onClick={props.onSkip}
            style={{
              ...announcementButtonBase,
              background: 'transparent',
              color: P.paper,
              borderColor: 'rgba(255,248,245,.55)',
            }}
          >
            {props.skipLabel}
          </button>
        )}
      </div>

      {props.gatedHint && (
        <p style={{ fontFamily: F.serif, fontStyle: 'italic', fontSize: 13, color: P.dek, margin: '10px 0 0' }}>
          {props.gatedHint}
        </p>
      )}
    </article>
  );
}
